import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {DateRangeSelectorComponent} from "../../../layout/date-range-selector/date-range-selector.component";

import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Partner} from '../../../../models/partner/partner';
import {forkJoin} from 'rxjs';
import {AdvertisingPlatform} from '../../../../models/advertising-platform';
import {AdvertisingPlatformService} from '../../../../services/advertising-platform/advertising-platform.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {PartnerService} from '../../../../services/partner/partner.service';
import {CostService} from '../../../../services/cost/cost.service';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {IsGranted} from '../../../../decorator/is-granted';
import {MetaTitle} from '../../../../decorator/meta-title';
import {CostDetailComponent} from './cost-detail/cost-detail.component';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {TableSorterHeaderDirective} from '../../../../directives/table-sorter/table-sorter-header.directive';
import {TableSortPipe} from '../../../../pipes/table-sort/table-sort.pipe';
import {FileDownloadService} from '../../../../services/file-download/file-download.service';
import {UserActivityService} from '../../../../services/user-activity/user-activity.service';

@Component({
  selector: 'app-cost-log',
  imports: [
    DateRangeSelectorComponent,
    ReactiveFormsModule,
    FormsModule,
    MoneyPipe,
    TableSorterDirective,
    TableSorterHeaderDirective,
    TableSortPipe
],
  templateUrl: './cost-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cost-log.component.scss'
})
@IsGranted(['COST_VIEW'])
@MetaTitle('Cost logs')
export class CostLogComponent implements OnInit {
  advertisingPlatforms: AdvertisingPlatform[] = [];
  partners: Partner[] = [];

  loading = true;

  rows: any[] = [];
  totals: any = {};

  tableSorter: TableSorter = new TableSorter('key', 'asc');

  currentFilters: any = {};

  filters: any = {
    partner: null,
    advertisingPlatform: null,
    groupBy: 'date-day',
  }

  @ViewChild(DateRangeSelectorComponent) dateSelector!: DateRangeSelectorComponent;

  constructor(
    private fileDownload: FileDownloadService,
    private userActivity: UserActivityService,
    private loader: LoaderService,
    private advertisingPlatformService: AdvertisingPlatformService,
    private partnerService: PartnerService,
    private costService: CostService,
    private dialog: DialogService
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.partnerService.findAll({sort: 'companyName', order: 'asc'}),
      this.advertisingPlatformService.getList()
    ]).subscribe(([partners, advertisingPlatforms]) => {
      this.partners = partners;
      this.advertisingPlatforms = advertisingPlatforms;
      this.loader.stopLoading();

      this.filter();
    });
  }

  filter(): void {
    this.loader.startLoading();

    this.currentFilters = {
      startDate: this.dateSelector.getDateISO8601From(),
      endDate: this.dateSelector.getDateISO8601To(),
      groupBy: this.filters.groupBy
    };

    if (this.filters.partner?.id) {
      this.currentFilters.partnerId = this.filters.partner?.id;
    }

    if (this.filters.advertisingPlatform) {
      this.currentFilters.advertisingPlatformId = this.filters.advertisingPlatform;
    }

    this.userActivity.reportFilters(this.currentFilters);
    this.costService.getStatistics(this.currentFilters).subscribe((data: any) => {
      this.rows = data.rows;
      this.totals = data.totals;
      this.loader.stopLoading();
      this.loading = false;
    });
  }

  getLabel(row: any): string {
    if (this.currentFilters.groupBy.startsWith('date-')) {
      return new Date(row.label).toLocaleDateString();
    }

    return row.label;
  }

  getGroupByColumnName(): string {
    switch (this.currentFilters.groupBy) {
      case 'partner':
        return 'Partners';
    }

    return 'Date';
  }

  showCostDetail(row: any): void {
    this.dialog.openDialog(CostDetailComponent, {
      data: {
        key: row.key,
        filters: this.currentFilters,
      },
      class: 'full-width'
    }).subscribe(() => {
    });
  }

  getNotAllocatedCosts(row: any): number {
    return row.costs - row.allocatedCosts;
  }

  getNotAllocatedCostsUsd(row: any): number {
    return row.costsUsd - row.allocatedCostsUsd;
  }

  downloadCsv(): void {
    if (this.loading || !this.rows.length) {
      return;
    }

    const headers = [
      this.getGroupByColumnName(),
      'Impressions',
      'Clicks',
      'Cost (EUR)',
      'Cost (USD)',
      'Not allocated (EUR)',
      'Not allocated (USD)',
    ];

    const csvRows: string[] = [headers.map(header => this.csvCell(header)).join(',')];

    for (const row of this.rows) {
      csvRows.push([
        this.csvCell(this.getLabel(row)),
        row.impressions ?? 0,
        row.clicks ?? 0,
        this.formatMoney(row.costs),
        this.formatMoney(row.costsUsd),
        this.formatMoney(this.getNotAllocatedCosts(row)),
        this.formatMoney(this.getNotAllocatedCostsUsd(row)),
      ].join(','));
    }

    csvRows.push([
      this.csvCell('Total'),
      this.totals.impressions ?? 0,
      this.totals.clicks ?? 0,
      this.formatMoney(this.totals.costs),
      this.formatMoney(this.totals.costsUsd),
      this.formatMoney(this.getNotAllocatedCosts(this.totals)),
      this.formatMoney(this.getNotAllocatedCostsUsd(this.totals)),
    ].join(','));

    this.triggerCsvDownload(csvRows.join('\n'), 'cost-log.csv');
  }

  private formatMoney(value: any): string {
    const input = parseFloat(value);
    return isNaN(input) ? '0.00' : (input / 100).toFixed(2);
  }

  private csvCell(value: any): string {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  }

  private triggerCsvDownload(content: string, filename: string): void {
    this.fileDownload.saveCsv(content, filename);
  }
}
